import * as React from "react";
import { useState } from "react";
import {
    Box,
    Container,
    Grid,
    Card,
    CardHeader,
    TextField,
    Button,
    Typography,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
    Divider,
    CircularProgress,
    Link as MUILink,
    ThemeProvider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";

import type { IContactForm } from "../utils/helpers/formHelpers";
import { handleContactFormChange, handleContactFormSubmit } from "../utils/helpers/formHelpers";

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL;

const Contact: React.FC = () => {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = React.useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const authState = useSelector((state: RootState) => state.auth);
    const isGuest = !authState.account;

    const [form, setForm] = useState<IContactForm>({
        type: null,
        subject: "",
        message: "",
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
        open: false,
        message: "",
        severity: "success",
    });

    const onCopyEmail = async () => {
        if (!SUPPORT_EMAIL) return;

        try {
            await navigator.clipboard.writeText(SUPPORT_EMAIL);
            setToast({ open: true, message: "Email address copied.", severity: "success" });
        } catch {
            setToast({ open: true, message: "Couldn't copy email.", severity: "error" });
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        handleContactFormSubmit(e, form, setToast, setForm, setSubmitting, setErrors);
    };

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    minHeight: "calc(100dvh - 68px)",
                    bgcolor: (t) => (t.palette.mode === "dark" ? "#0b0d10" : "#fafafa"),
                    py: { xs: 6, md: 10 },
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <Container maxWidth="md">
                    <Card
                        elevation={0}
                        sx={{
                            overflow: "hidden",
                            borderRadius: 5,
                            border: (t) => `1px solid ${t.palette.divider}`,
                            bgcolor: (t) => (t.palette.mode === "dark" ? "#0f1318" : "#ffffff"),
                        }}
                    >
                        <Grid container>
                            {/* Left: Contact Info */}
                            <Grid
                                size={12}
                                sx={{
                                    background: (t) =>
                                        t.palette.mode === "dark"
                                            ? "linear-gradient(180deg, rgba(35, 40, 47, 0.9), rgba(20, 24, 29, 0.9))"
                                            : "linear-gradient(180deg, #fff7ed, #fff)",
                                    p: { xs: 4, md: 5 },
                                    borderRight: { md: (t) => `1px solid ${t.palette.divider}` },
                                }}
                            >
                                {SUPPORT_EMAIL ? (
                                    <>
                                        <CardHeader
                                            titleTypographyProps={{ variant: "h5", fontWeight: 700 }}
                                            title="Get in touch"
                                            subheader={"We'd love to hear from you—bugs, ideas, or just to say hi."}
                                            sx={{ p: 0, mb: 3 }}
                                        />

                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                            <EmailIcon fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Email us directly:
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {SUPPORT_EMAIL}
                                            </Typography>
                                            <Tooltip title="Copy email">
                                                <IconButton onClick={onCopyEmail} size="small" aria-label="Copy email address">
                                                    <ContentCopyIcon fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                            <Button
                                                component={MUILink}
                                                href={`mailto:${SUPPORT_EMAIL}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                startIcon={<EmailIcon />}
                                                sx={{ textTransform: "none" }}
                                            >
                                                Write an email
                                            </Button>
                                        </Box>

                                        <Divider sx={{ my: 3 }} />

                                        <Typography variant="body2" color="text.secondary">
                                            Prefer a quick note? Use the form →
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="h4" sx={{ width: "100%", textAlign: "center" }}>
                                        Something went wrong! Please try again later.
                                    </Typography>
                                )}
                            </Grid>

                            {/* Right: Form */}
                            {!isGuest && SUPPORT_EMAIL && (
                                <Grid size={12}>
                                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ p: { xs: 4, md: 5 } }}>
                                        <FormControl fullWidth sx={{ mb: 2 }}>
                                            <InputLabel id="contact-type-label">Type</InputLabel>
                                            <Select
                                                labelId="contact-type-label"
                                                value={form.type ?? ""}
                                                label="Type"
                                                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as IContactForm["type"] }))}
                                                variant="standard"
                                            >
                                                <MenuItem value="bug">Bug</MenuItem>
                                                <MenuItem value="addition">Addition</MenuItem>
                                                <MenuItem value="support">Support</MenuItem>
                                                <MenuItem value="message">Message</MenuItem>
                                                <MenuItem value="other">Other</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                                            Send us a message
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid size={12}>
                                                <TextField
                                                    label="Subject"
                                                    value={form.subject}
                                                    onChange={handleContactFormChange("subject", setForm)}
                                                    error={Boolean(errors.subject)}
                                                    helperText={errors.subject}
                                                    variant="filled"
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <TextField
                                                    label="Message"
                                                    value={form.message}
                                                    onChange={handleContactFormChange("message", setForm)}
                                                    error={Boolean(errors.message)}
                                                    helperText={errors.message}
                                                    fullWidth
                                                    multiline
                                                    variant="filled"
                                                    minRows={8}
                                                    maxRows={8}
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        size="large"
                                                        startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                                                        disabled={submitting}
                                                        sx={{ borderRadius: 999, px: 3 }}
                                                    >
                                                        {submitting ? "Sending…" : "Send"}
                                                    </Button>
                                                    <Typography variant="caption" color="text.secondary">
                                                        We reply within 1–2 business days.
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Card>

                    <Box sx={{ textAlign: "center", mt: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            By contacting us, you agree to our handling of your data per our privacy policy.
                        </Typography>
                    </Box>

                    <Snackbar
                        open={toast.open}
                        autoHideDuration={3500}
                        onClose={() => setToast((t) => ({ ...t, open: false }))}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    >
                        <Alert
                            onClose={() => setToast((t) => ({ ...t, open: false }))}
                            severity={toast.severity}
                            variant="filled"
                            sx={{ width: "100%" }}
                        >
                            {toast.message}
                        </Alert>
                    </Snackbar>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default Contact;
